import { apiClient } from '@/lib/api-client';
import type { Appointment } from '@/types/appointment';
import type { AppointmentStatus } from '@/types/appointment-status';
import type { CalendarBlock } from '@/types/calendar-block';
import type { Service, ServiceCategory } from '@/types/service';
import type { TechnicianSchedule } from '@/types/technician-schedule';
import type { User } from '@/types/user';

export interface AppointmentFilters {
  from?: string;
  to?: string;
  technicianId?: string;
  serviceId?: string;
  customerId?: string;
  status?: AppointmentStatus[];
}

export interface CreateAppointmentPayload {
  serviceId: string;
  customerId: string;
  technicianId: string;
  date: string;
  startTime: string;
  status?: AppointmentStatus;
  notes?: string;
}

export interface UpdateAppointmentPayload {
  serviceId?: string;
  customerId?: string;
  technicianId?: string;
  date?: string;
  startTime?: string;
  status?: AppointmentStatus;
  notes?: string;
}

export interface AvailabilitySlot {
  startTime: string;
  endTime: string;
}

export type AvailabilityResponse =
  | {
      durationMinutes: number;
      availableSlots: AvailabilitySlot[];
    }
  | {
      available: boolean;
      endTime?: string;
      reason?: string;
    };

export interface AvailabilityQuery {
  serviceId: string;
  technicianId: string;
  date: string;
  startTime?: string;
  appointmentId?: string;
}

export interface CalendarBlockPayload {
  technicianId: string;
  date: string;
  startTime: string;
  endTime: string;
  reason?: string;
}

export interface CalendarBlockFilters {
  from?: string;
  to?: string;
  technicianId?: string;
}

export interface TechnicianScheduleEntry {
  weekday: number;
  startTime: string;
  endTime: string;
}

export const fetchServices = async (): Promise<Service[]> => {
  const response = await apiClient.get<Service[]>('/agenda-servicos/servicos');
  return response.data;
};

export const fetchServiceCategories = async (): Promise<ServiceCategory[]> => {
  const response = await apiClient.get<ServiceCategory[]>('/agenda-servicos/categorias');
  return response.data;
};

export interface ServicePayload {
  name: string;
  durationMinutes: number;
  price: number;
  categoryId?: string;
}

export const createService = async (payload: ServicePayload): Promise<Service> => {
  const response = await apiClient.post<Service>('/agenda-servicos/servicos', payload);
  return response.data;
};

export interface CreateServiceCategoryPayload {
  name: string;
  colorHex?: string;
}

export const createServiceCategory = async (
  payload: CreateServiceCategoryPayload,
): Promise<ServiceCategory> => {
  const response = await apiClient.post<ServiceCategory>(
    '/agenda-servicos/categorias',
    payload,
  );
  return response.data;
};

export const updateService = async (
  id: string,
  payload: Partial<ServicePayload>,
): Promise<Service> => {
  const response = await apiClient.patch<Service>(`/agenda-servicos/servicos/${id}`, payload);
  return response.data;
};

export const deleteService = async (id: string): Promise<void> => {
  await apiClient.delete(`/agenda-servicos/servicos/${id}`);
};

export const fetchAppointments = async (filters: AppointmentFilters): Promise<Appointment[]> => {
  const response = await apiClient.get<Appointment[]>('/agenda-servicos/compromissos', {
    params: filters,
  });
  return response.data;
};

export const createAppointment = async (payload: CreateAppointmentPayload): Promise<Appointment> => {
  const response = await apiClient.post<Appointment>('/agenda-servicos/compromissos', payload);
  return response.data;
};

export const updateAppointment = async (
  id: string,
  payload: UpdateAppointmentPayload,
): Promise<Appointment> => {
  const response = await apiClient.patch<Appointment>(
    `/agenda-servicos/compromissos/${id}`,
    payload,
  );
  return response.data;
};

export const cancelAppointment = async (id: string): Promise<Appointment> => {
  const response = await apiClient.post<Appointment>(`/agenda-servicos/compromissos/${id}/cancel`);
  return response.data;
};

export const completeAppointment = async (id: string): Promise<Appointment> => {
  const response = await apiClient.post<Appointment>(
    `/agenda-servicos/compromissos/${id}/complete`,
  );
  return response.data;
};

export const fetchAvailability = async (
  query: AvailabilityQuery,
): Promise<AvailabilityResponse> => {
  const response = await apiClient.get<AvailabilityResponse>(
    '/agenda-servicos/compromissos/availability',
    { params: query },
  );
  return response.data;
};

export const fetchCalendarBlocks = async (
  filters: CalendarBlockFilters,
): Promise<CalendarBlock[]> => {
  const response = await apiClient.get<CalendarBlock[]>('/agenda-servicos/bloqueios', {
    params: filters,
  });
  return response.data;
};

export const createCalendarBlock = async (
  payload: CalendarBlockPayload,
): Promise<CalendarBlock> => {
  const response = await apiClient.post<CalendarBlock>(
    '/agenda-servicos/bloqueios',
    payload,
  );
  return response.data;
};

export const updateCalendarBlock = async (
  id: string,
  payload: Partial<CalendarBlockPayload>,
): Promise<CalendarBlock> => {
  const response = await apiClient.patch<CalendarBlock>(
    `/agenda-servicos/bloqueios/${id}`,
    payload,
  );
  return response.data;
};

export const deleteCalendarBlock = async (id: string): Promise<void> => {
  await apiClient.delete(`/agenda-servicos/bloqueios/${id}`);
};

export const fetchTechnicians = async (): Promise<User[]> => {
  const response = await apiClient.get<User[]>('/agenda-servicos/tecnicos');
  return response.data;
};

export const fetchTechnicianSchedules = async (
  technicianId: string,
): Promise<TechnicianSchedule[]> => {
  const response = await apiClient.get<TechnicianSchedule[]>(
    `/agenda-servicos/horarios-tecnicos/${technicianId}`,
  );
  return response.data;
};

export const replaceTechnicianSchedules = async (
  technicianId: string,
  entries: TechnicianScheduleEntry[],
): Promise<TechnicianSchedule[]> => {
  const response = await apiClient.put<TechnicianSchedule[]>(
    `/agenda-servicos/horarios-tecnicos/${technicianId}`,
    { entries },
  );
  return response.data;
};
